import clsx from 'clsx'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement } from 'react'
import { forwardRef } from 'react'
import styled from 'styled-components'
import SearchTileFeaturedMatchBanner from './shared/SearchTileFeaturedMatchBanner'
import {
  metaSearchTileContainerQueryUp,
  searchTileFeaturedMatchBorderStyles,
  searchTileLayoutStyles,
  searchTileResponsiveStateBorderStyles,
} from './searchTileLayoutStyles'

const Container = styled.div`
  container: search-tile / inline-size;
  display: grid;
  grid-template: 1fr / 1fr;
`

export const _searchTileContainerQueryUp = metaSearchTileContainerQueryUp

const LayoutWrapper = styled.div<{ $treatment: SearchTileTreatment }>`
  display: flex;
  flex-direction: column;

  ${searchTileFeaturedMatchBorderStyles}

  ${searchTileResponsiveStateBorderStyles}
`

const Layout = styled.div<{ $density: SearchTileDensity, $layoutHeight: 'meta-search' | 'search' }>`
  ${props => searchTileLayoutStyles(props.$layoutHeight === 'search' ? rem(300) : rem(178))}
`

const Banner = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  /**
   * @default default
   */
  state?: 'default' | 'highlighted'
  banner?: ReactElement | false
  className?: string
  /**
   * Presentation-only density for accommodation SERP compact experiment tiles.
   * @default default
   */
  density?: SearchTileDensity
  /**
   * @default meta-search
   */
  layoutHeight?: 'meta-search' | 'search'
  /**
   * Presentation-only treatment for featured/matched search result tiles.
   * @default default
   */
  treatment?: SearchTileTreatment
}

const _MetaSearchTileBase = forwardRef<HTMLDivElement, Props>(function MetaSearchTileBase(
  {
    state = 'default',
    banner,
    className,
    density = 'default',
    layoutHeight = 'meta-search',
    treatment = 'default',
    children,
    ...rest
  },
  ref,
) {
  const hugContent = useMetaSearchMobileV2Enabled()
  const isFeaturedMatch = treatment === 'featured-match'
  // Compact density forces a tall min-height that leaves empty space under the
  // price row on mobile V2; use default layout height so the tile hugs content.
  const layoutDensity = hugContent ? 'default' : density

  return <Container {...rest} ref={ref} className={clsx('_MetaSearchTileBase', className)} data-testid="MetaSearchTileBase">
    <LayoutWrapper className={`state-${state}`} $treatment={treatment}>
      {(isFeaturedMatch || !!banner) && <Banner>
        {isFeaturedMatch && <SearchTileFeaturedMatchBanner />}
        {banner}
      </Banner>}
      <Layout $density={layoutDensity} $layoutHeight={layoutHeight}>
        {children}
      </Layout>
    </LayoutWrapper>
  </Container>
})

export default _MetaSearchTileBase
